import { Form, SubmitButton, TextAreaInputField } from '@/components';
import { useUpdateRemark } from '@/features/project/_hooks/project';
import { RelatedMaterialFormSchema } from '@/features/project/projects/_models/related-material';
import { useForm, useId } from '@/hooks';
import { useMessage } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import type { IndonesiaData } from '../../../_models/indonesia-data';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';

type FormValues = z.infer<typeof RelatedMaterialFormSchema>;

type Props = {
  initialValues: Partial<FormValues>;
  initialIndonesiaData: IndonesiaData | undefined;
};

export const RelatedMaterialForm: FC<Props> = ({
  initialValues,
  initialIndonesiaData,
}) => {
  const { t } = useTranslation();
  const { mutateAsync: updateProject } = useUpdateRemark();
  const { id } = useId();
  const { openMessage } = useMessage();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty, errors, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(RelatedMaterialFormSchema),
  });
  const { setDrawerState } = useProjectDetailDrawerState();

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(async (values: FormValues) => {
        try {
          await updateProject({
            id,
            indonesiaData: {
              ...initialIndonesiaData,
              evaluationOfProgramResults: values.evaluationOfProgramResults,
              usulanPerbaikan: values.usulanPerbaikan,
              referensiProgramYangSamaDiDaerahLain:
                values.referensiProgramYangSamaDiDaerahLain,
              catatan: values.catatan,
            },
          });
          openMessage('success', 'success');
          setDrawerState({ open: false });
        } catch {
          openMessage('error', 'failed');
        }
      })}
    >
      <TextAreaInputField
        name="evaluationOfProgramResults"
        label={t('evaluation-of-program-results')}
        control={control}
        errors={errors}
        rows={3}
      />
      <TextAreaInputField
        name="usulanPerbaikan"
        label={t('improvement-suggestions')}
        control={control}
        errors={errors}
        rows={3}
      />
      <TextAreaInputField
        name="referensiProgramYangSamaDiDaerahLain"
        label={t('related-program-for-other-ministry')}
        control={control}
        errors={errors}
        rows={3}
      />
      <TextAreaInputField
        name="catatan"
        label={t('catatan')}
        control={control}
        errors={errors}
        rows={3}
      />
      <SubmitButton
        text={t('save')}
        loading={isSubmitting}
        disabled={!isDirty}
      />
    </Form>
  );
};
